import { format, parseISO } from "date-fns";

const TimeLogStatus = ({ status, isPDF = false }) => {
  const statusStyles = {
    approved: "bg-green-100 text-green-800",
    rejected: "bg-red-100 text-red-800",
    pending: "bg-yellow-100 text-yellow-800",
  };

  return (
    <span
      className={`px-3 py-1 ${
        isPDF ? "pb-4" : ""
      } rounded-full text-xs font-medium status-pill ${
        statusStyles[status]
      } flex justify-center items-center`}
    >
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
};

const StudentTimeLogsDetails = ({
  studentTimeLogs,
  studentOpportunities,
  student,
  isPDF = false,
}) => {
  const calculateHours = (startTime, endTime) => {
    const [startHour, startMinute] = startTime.split(":").map(Number);
    const [endHour, endMinute] = endTime.split(":").map(Number);

    const totalMinutes =
      endHour * 60 + endMinute - (startHour * 60 + startMinute);
    return totalMinutes / 60;
  };

  const calculateTotalApprovedHours = (logs) => {
    const total = logs
      .filter((log) => log.status === "approved")
      .reduce((sum, log) => {
        const hours = calculateHours(log.startTime, log.endTime);
        return sum + hours;
      }, 0);

    return Number(total.toFixed(1));
  };

  const renderOpportunityTimeLogs = (opportunity) => {
    const opportunityLogs = studentTimeLogs.filter(
      (log) =>
        log.opportunityId === opportunity.id &&
        log.studentId === student.studentId
    );

    const totalApprovedHours = opportunityLogs
      .filter((log) => log.status === "approved")
      .reduce(
        (sum, log) => sum + calculateHours(log.startTime, log.endTime),
        0
      );

    return (
      <div
        key={opportunity.id}
        className={`mb-2 ${
          isPDF ? "" : "bg-gray-50"
        } p-2 rounded-lg avoid-page-break`}
      >
        <h4 className="text-lg font-semibold text-gray-800">
          {opportunity.hostName}
          <span className="text-sm font-normal text-gray-600 ml-2">
            ({opportunity.opportunityType})
          </span>
        </h4>

        <div className="space-y-2 avoid-page-break">
          {opportunityLogs.length > 0 ? (
            <>
              <div className="text-sm text-gray-600 mb-4">
                Total Approved Hours: {totalApprovedHours.toFixed(1)}
              </div>
              {opportunityLogs.map((log, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between bg-white p-3 rounded border border-gray-200 avoid-page-break"
                >
                  <div className={`flex-1 ${isPDF ? "pb-2" : ""}`}>
                    <div className="text-sm">
                      {format(parseISO(log.date), "MMM dd, yyyy")}
                    </div>
                    <div className="text-sm text-gray-600">
                      {log.startTime} - {log.endTime}
                      <span className="ml-2">
                        ({calculateHours(log.startTime, log.endTime).toFixed(1)}{" "}
                        hours)
                      </span>
                    </div>
                  </div>
                  <TimeLogStatus status={log.status} isPDF={isPDF} />
                </div>
              ))}
            </>
          ) : (
            <p className="text-sm text-gray-500 italic">
              No time logs recorded
            </p>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <div className="bg-blue-50 p-4 rounded-lg mb-6">
        <p className="text-md font-semibold text-blue-800">
          Total Completed Hours:{" "}
          <span className="text-lg">
            {calculateTotalApprovedHours(studentTimeLogs)}
          </span>
        </p>
      </div>

      {studentOpportunities.length > 0 ? (
        <div>
          {studentOpportunities.map((opportunity) =>
            renderOpportunityTimeLogs(opportunity)
          )}
        </div>
      ) : (
        <p className="text-gray-500 italic">No opportunities available.</p>
      )}
    </div>
  );
};

export default StudentTimeLogsDetails;
